import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { FiWifi, FiHardDrive, FiRadio, FiSettings } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Services = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: 'Fiber Internet',
      icon: FiWifi,
      description: 'Ultra-fast, reliable fiber optic connectivity for businesses that demand the best performance.',
      features: [
        'Speeds up to 10 Gbps',
        '99.99% uptime guarantee',
        'Symmetrical upload/download',
        'Low latency connections',
        'Dedicated fiber line'
      ]
    },
    {
      title: 'Cable Internet',
      icon: FiHardDrive,
      description: 'High-speed cable internet solutions with extensive coverage and proven reliability.',
      features: [
        'Speeds up to 1 Gbps',
        'Wide availability',
        'Cost-effective solution',
        'Quick installation',
        '24/7 technical support'
      ]
    },
    {
      title: 'Fixed Wireless',
      icon: FiRadio,
      description: 'Advanced wireless broadband perfect for areas with limited wired infrastructure.',
      features: [
        'Speeds up to 500 Mbps',
        'No cable required',
        'Quick deployment',
        'Scalable solutions',
        'Weather-resistant equipment'
      ]
    },
    {
      title: 'Managed Services',
      icon: FiSettings,
      description: 'Complete network management and monitoring to keep your business running smoothly.',
      features: [
        '24/7 network monitoring',
        'Proactive maintenance',
        'Security management',
        'Performance optimization',
        'Dedicated support team'
      ]
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Complete Business{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
              Internet Solutions
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We connect you with the best internet services for your business needs and budget
          </p>
        </motion.div>

        {/* Video Embed */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <div style={{position: 'relative', aspectRatio: '16/9'}}>
            <iframe 
              loading="lazy" 
              title="Gumlet video player"
              src="https://play.gumlet.io/embed/69269270b7ac8e3f140a6548?background=false&autoplay=false&loop=false&disableControls=false"
              style={{border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%'}}
              referrerPolicy="origin"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen;"
            />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <SafeIcon icon={service.icon} className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
              <p className="text-gray-600 mb-4">{service.description}</p>
              <ul className="space-y-2">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-2">
                    <SafeIcon icon={FiIcons.FiCheck} className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Not sure which service is right for your business?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Our experts will analyze your needs and recommend the best solution at the best price
            </p>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Get Expert Recommendation
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;